import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../services/api';
import ServiceAgreementPanel from '../components/ServiceAgreementPanel';
import {
  MENTEE_KEY_STORAGE,
  REGISTRATION_PATHWAY,
  REGISTRATION_PATHWAY_STORAGE,
  SCREENS,
  isDemoCreationCode,
  isProBriefingKey,
  normalizeClientKey,
} from '../lib/screenRegistry';
import { isMasterTestingKey } from '../lib/masterTestingKey';
import './RegisterMenteeCode.css';
import '../styles/registrationSplit.css';

const LOGO = encodeURI('/Assets/logo/Mentology Logo.png');

export default function RegisterMenteeCode() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefilledCode = (location.state as { demoCode?: string } | null)?.demoCode ?? '';
  const { refresh, login, masterSandboxUnlocked } = useAuth();
  const [code, setCode] = useState(prefilledCode);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (prefilledCode) {
      setCode(prefilledCode);
    }
  }, [prefilledCode]);

  useEffect(() => {
    if (window.self === window.top) return undefined;

    document.documentElement.classList.add('maintenance-embed-preview');
    document.body.classList.add('maintenance-embed-preview');
    document.getElementById('root')?.classList.add('maintenance-embed-preview');

    return () => {
      document.documentElement.classList.remove('maintenance-embed-preview');
      document.body.classList.remove('maintenance-embed-preview');
      document.getElementById('root')?.classList.remove('maintenance-embed-preview');
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!agreed && !masterSandboxUnlocked && !isMasterTestingKey(code)) {
      setError('The mandatory Service Agreement declaration must be accepted before verification.');
      return;
    }

    setLoading(true);
    setError('');

    const normalizedCode = normalizeClientKey(code);

    try {
      sessionStorage.setItem(REGISTRATION_PATHWAY_STORAGE, REGISTRATION_PATHWAY.INVITATION);

      if (isMasterTestingKey(code.trim())) {
        await login(code.trim());
        await refresh();
        navigate('/master-admin', { replace: true });
        return;
      }

      if (!isDemoCreationCode(normalizedCode) && !isMasterTestingKey(code.trim())) {
        setError('Invalid demo key format. Expected PRO-XXX or ELT-XXX (3 letters).');
        setLoading(false);
        return;
      }

      const result = await authApi.registerCode({ code: normalizedCode, email, name });
      const token = result.demoAccessKey
        ? normalizeClientKey(result.demoAccessKey)
        : result.userToken
          ? normalizeClientKey(result.userToken)
          : normalizedCode;
      sessionStorage.setItem(MENTEE_KEY_STORAGE, token);

      await refresh();

      if (isProBriefingKey(token)) {
        navigate('/mentee-lounge', { replace: true });
        return;
      }

      navigate('/introduction', { replace: true });
    } catch (err: unknown) {
      setError((err as { error?: string }).error || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="registration-split-screen register-mentee-code-screen flex flex-col md:flex-row w-full items-stretch md:items-start"
      data-screen-id={SCREENS.PRO02}
    >
      <aside
        className="registration-split-brand w-full md:w-[30%] flex flex-col items-center p-6 bg-transparent overflow-hidden"
        aria-label="Mentology brand"
      >
        <div className="registration-split-logo-anchor">
          <img
            src={LOGO}
            alt="Mentology"
            className="registration-split-logo"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      </aside>

      <main className="registration-split-workspace registration-split-workspace--demo-scroll w-full md:w-[70%] flex flex-col p-8">
        <div className="modal-box register-mentee-code-panel">
          <h2 className="section-header register-mentee-code-heading">
            Mentee Demo Mode Setup
          </h2>

          <form onSubmit={handleSubmit} className="register-mentee-code-form" noValidate>
            <div className="demo-key-entry-field">
              <label htmlFor="pro02-demo-key" className="demo-key-entry-label">
                Enter Demo Key
              </label>
              <p className="demo-key-entry-format">(PRO-XXX / ELT-XXX)</p>
              <input
                id="pro02-demo-key"
                className="input-field"
                placeholder="Enter Demo Key"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                aria-label="Enter Demo Key"
              />
            </div>
            <input
              className="input-field"
              placeholder="Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <input
              className="input-field"
              type="email"
              placeholder="Secure Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <ServiceAgreementPanel agreed={agreed} onAgreedChange={setAgreed} variant="compact" />

            {error && <p className="error-text">{error}</p>}

            <div className="button-control-group">
              <button type="submit" className="btn-primary" disabled={loading || (!agreed && !masterSandboxUnlocked && !isMasterTestingKey(code))}>
                {loading ? 'Verifying...' : 'Verify & Enter Briefing Room'}
              </button>
              <Link to="/register-code" className="btn-primary">Back</Link>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
